import { create } from 'zustand'

export interface AppNotification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message?: string
  isRead: boolean
  createdAt: Date
  link?: string
  resourceType?: string
  resourceId?: string
}

interface NotificationState {
  notifications: AppNotification[]
  unreadCount: number
  addNotification: (notif: Omit<AppNotification, 'id' | 'isRead' | 'createdAt'>) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  notifications: [],
  unreadCount: 0,

  addNotification: (notif) => {
    const newNotif: AppNotification = {
      ...notif,
      id:        `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      isRead:    false,
      createdAt: new Date(),
    }
    set((state) => ({
      notifications: [newNotif, ...state.notifications].slice(0, 100),
      unreadCount:   state.unreadCount + 1,
    }))
    // Auto-remove après 5s pour success/info, 8s pour error/warning
    const delay = (notif.type === 'error' || notif.type === 'warning') ? 8000 : 5000
    setTimeout(() => {
      get().removeNotification(newNotif.id)
    }, delay)
  },

  markAsRead: (id) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n,
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }))
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }))
  },

  removeNotification: (id) => {
    const notif = get().notifications.find((n) => n.id === id)
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount:   notif && !notif.isRead
        ? Math.max(0, state.unreadCount - 1)
        : state.unreadCount,
    }))
  },

  clearAll: () => set({ notifications: [], unreadCount: 0 }),
}))
